import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AIReceptionist() {
  const [activeServiceModal, setActiveServiceModal] = useState<string | null>(null);
  const [showIndustriesModal, setShowIndustriesModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const faqs = [
    {
      question: "How does an AI Receptionist differ from a regular chatbot or phone system?",
      answer: "An AI Receptionist is a comprehensive solution that combines both chat and voice capabilities into one unified system. Unlike basic chatbots that only handle text, or simple phone systems that just route calls, our AI Receptionist can handle phone calls with natural conversation, manage live chat on your website, book appointments across both channels, qualify leads, answer questions, and seamlessly transfer to your team when needed. It's like having a full-time receptionist who works 24/7 across all communication channels."
    },
    {
      question: "Can the AI Receptionist handle both phone calls and website chat simultaneously?",
      answer: "Yes! Our AI Receptionist is designed to handle multiple conversations across different channels at the same time. It can answer phone calls while simultaneously responding to website chat messages, ensuring no customer is left waiting. Each interaction is handled with the same level of professionalism and attention, whether it's a phone call, chat message, or even SMS inquiry."
    },
    {
      question: "How natural does the AI voice sound on phone calls?",
      answer: "Our AI Receptionist uses advanced voice technology that sounds remarkably human-like. It can understand natural speech patterns, handle interruptions, respond with appropriate tone and emotion, and even adapt its speaking style to match your brand. Most callers don't realize they're speaking with AI. The voice is clear, professional, and can be customized to match your business personality—whether you want friendly and casual or formal and professional."
    },
    {
      question: "Can the AI Receptionist book appointments and sync with my calendar?",
      answer: "Absolutely! The AI Receptionist integrates directly with popular calendar systems like Google Calendar, Outlook, and Apple Calendar. It can check your real-time availability, book appointments, send confirmation emails, handle rescheduling requests, and send automated reminders. Whether a customer calls or chats, they can book an appointment instantly without waiting for your team to respond."
    },
    {
      question: "What happens if the AI can't answer a question or needs to transfer to a human?",
      answer: "The AI Receptionist is smart enough to recognize when it needs human assistance. If it encounters a complex question, an upset customer, or a situation requiring human judgment, it can seamlessly transfer the call or chat to your team with full context of the conversation. You can also set custom rules for when transfers should happen, such as high-value leads, specific service requests, or VIP customers."
    },
    {
      question: "How does the AI Receptionist qualify leads?",
      answer: "The AI Receptionist can ask qualifying questions based on your criteria, such as budget, timeline, specific needs, location, or service interest. It collects this information naturally during the conversation and logs it in your CRM. You can customize the qualification criteria, and the AI will prioritize hot leads, schedule immediate callbacks for high-value prospects, and nurture leads that need more time."
    },
    {
      question: "Can I customize what the AI Receptionist says and how it responds?",
      answer: "Yes! You have full control over your AI Receptionist's personality, responses, and behavior. You can customize greetings, FAQs, appointment booking flows, qualification questions, transfer rules, and even the voice tone. We provide an easy-to-use dashboard where you can update responses, add new information, and train the AI on your specific business knowledge without any technical skills required."
    },
    {
      question: "How much does an AI Receptionist cost compared to hiring a human receptionist?",
      answer: "An AI Receptionist typically costs a fraction of hiring a full-time receptionist. While a human receptionist costs $30,000-$45,000 per year (plus benefits, training, and time off), our AI Receptionist starts at a few hundred dollars per month and works 24/7 without breaks, sick days, or vacations. You get unlimited availability, consistent quality, and the ability to handle multiple conversations simultaneously—something impossible with a single human receptionist."
    }
  ];

  const features = [
    {
      icon: 'ri-phone-line',
      title: 'Natural Voice Calls',
      description: 'Handle phone calls with human-like conversation, understanding context and responding naturally to customer inquiries.'
    },
    {
      icon: 'ri-message-3-line',
      title: 'Live Chat Support',
      description: 'Respond to website chat messages instantly, providing immediate assistance to visitors browsing your site.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Appointment Booking',
      description: 'Schedule appointments across phone and chat channels with real-time calendar sync and automated confirmations.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Lead Qualification',
      description: 'Ask the right questions to qualify leads, collect important information, and prioritize high-value prospects.'
    },
    {
      icon: 'ri-time-line',
      title: '24/7 Availability',
      description: 'Never miss a call or chat message. Your AI Receptionist works around the clock, including nights, weekends, and holidays.'
    },
    {
      icon: 'ri-share-forward-line',
      title: 'Smart Call Transfer',
      description: 'Seamlessly transfer calls to your team when needed, with full conversation context and customer information.'
    }
  ];

  const benefits = [
    {
      title: 'Reduce Staffing Costs',
      description: 'Save thousands per year compared to hiring full-time receptionists while getting 24/7 coverage.'
    },
    {
      title: 'Never Miss Opportunities',
      description: 'Capture every lead and appointment, even during busy times, after hours, or when your team is unavailable.'
    },
    {
      title: 'Improve Customer Experience',
      description: 'Provide instant responses and professional service across all channels, enhancing customer satisfaction.'
    },
    {
      title: 'Scale Without Limits',
      description: 'Handle unlimited simultaneous conversations without hiring additional staff or expanding your team.'
    },
    {
      title: 'Focus on Core Business',
      description: 'Free your team from routine inquiries so they can focus on high-value activities and customer relationships.'
    }
  ];

  return (
    <>
      <SEO
        title="AI Receptionist | Virtual Front Desk Automation"
        description="Upgrade your front desk with an AI receptionist. Handle calls, greet visitors, schedule appointments, and manage inquiries with intelligent automation."
        canonical="/services/ai-receptionist"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Receptionist',
              description: 'Virtual AI receptionist for automated front desk operations and customer service',
              provider: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: import.meta.env.VITE_SITE_URL || 'https://example.com'
              },
              areaServed: 'Worldwide',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/ai-receptionist`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'What can an AI receptionist do?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'An AI receptionist can answer calls, greet visitors, schedule appointments, answer common questions, route inquiries, and provide information about your business 24/7.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Is AI receptionist better than a human receptionist?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI receptionists complement human staff by handling routine tasks 24/7, never taking breaks, and managing multiple interactions simultaneously, while humans handle complex situations requiring empathy and judgment.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'How much does an AI receptionist cost?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI receptionist services are typically more cost-effective than hiring full-time staff, with flexible pricing plans based on usage and features needed.'
                  }
                }
              ]
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
          <div className="absolute inset-0 opacity-30">
            <div 
              className="w-full h-full bg-cover bg-center"
              style={{
                backgroundImage: `url('/readdy-assets/ai-receptionist-hero-001.jpg')`
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/80"></div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-display text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                AI Receptionist for Small Businesses
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Your 24/7 virtual receptionist that handles phone calls, live chat, appointment booking, and lead qualification—all with human-like conversation and professionalism.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Your AI Receptionist
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </section>

        {/* What is AI Receptionist Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">
                  What is an AI Receptionist?
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    An AI Receptionist is an intelligent virtual assistant that combines the capabilities of a traditional receptionist with advanced artificial intelligence. Unlike basic chatbots or simple phone systems, an AI Receptionist provides comprehensive front-desk services across multiple communication channels.
                  </p>
                  <p>
                    Think of it as your business's first point of contact—answering phone calls with natural conversation, responding to website <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">chat messages</Link>, booking appointments, qualifying leads, answering frequently asked questions, and transferring important calls to your team. All of this happens 24/7, without breaks, sick days, or vacations.
                  </p>
                  <p>
                    For small businesses, an AI Receptionist is a game-changer. It provides enterprise-level customer service at a fraction of the cost of hiring full-time staff. Whether you're a dental office, law firm, home services company, or retail business, an AI Receptionist ensures every customer interaction is handled professionally and efficiently. Pair it with our <Link to="/services/ai-lead-follow-up" className="text-primary hover:underline font-medium">AI lead follow-up</Link> to convert every caller into a booked appointment.
                  </p>
                  <p>
                    The technology uses natural language processing to understand customer intent, context-aware responses to provide relevant information, and seamless integration with your existing tools like calendars, CRMs, and phone systems. The result is a customer experience that feels personal and professional, while you save time and money.
                  </p>
                </div>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/readdy-assets/ai-receptionist-what-001.jpg"
                  alt="AI Receptionist Interface"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                How Our AI Receptionist Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A seamless, intelligent system that handles all your customer interactions with professionalism and efficiency.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Customer Contacts You',
                  description: 'A customer calls your business number or starts a chat on your website. The AI Receptionist answers instantly, greeting them professionally.'
                },
                {
                  step: '02',
                  title: 'Natural Conversation',
                  description: 'The AI engages in natural conversation, understanding the customer\'s needs, answering questions, and collecting relevant information.'
                },
                {
                  step: '03',
                  title: 'Takes Action',
                  description: 'Based on the conversation, the AI books appointments, qualifies leads, provides information, or transfers to your team when needed.'
                },
                {
                  step: '04',
                  title: 'Logs & Follows Up',
                  description: 'All interactions are logged in your CRM, appointments are confirmed, and automated follow-ups are sent to ensure nothing falls through the cracks.'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow h-full">
                    <div className="text-5xl font-bold text-primary/20 mb-4">{item.step}</div>
                    <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-2xl text-primary"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Powerful Features for Complete Reception Coverage
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to provide professional, efficient customer service across all channels.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-teal-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Benefits for Your Small Business
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your customer service while reducing costs and improving efficiency.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-gray-900 mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Explore Related Services
              </h2>
              <p className="text-lg text-gray-600">
                Enhance your AI Receptionist with these complementary solutions
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Link
                to="/services/ai-chatbot"
                className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-chat-3-line text-2xl text-white"></i>
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                  AI Chatbot
                </h3>
                <p className="text-gray-600 mb-4">
                  Engage website visitors with intelligent chat support and lead capture.
                </p>
                <span className="text-primary font-semibold inline-flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </Link>

              <Link
                to="/ai-phone-answering-service"
                className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">
                  Phone Answering Service
                </h3>
                <p className="text-gray-600 mb-4">
                  Never miss a call with AI-powered phone answering and call routing.
                </p>
                <span className="text-secondary font-semibold inline-flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </Link>

              <Link
                to="/ai-appointment-scheduling"
                className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                  Appointment Scheduling
                </h3>
                <p className="text-gray-600 mb-4">
                  Automate booking with calendar sync and automated reminders.
                </p>
                <span className="text-primary font-semibold inline-flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about AI Receptionist services
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-gray-900 text-lg pr-4">
                      {faq.question}
                    </span>
                    <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-2xl text-primary flex-shrink-0`}></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-8 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary to-secondary text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div 
              className="w-full h-full bg-cover bg-center"
              style={{
                backgroundImage: `url('/readdy-assets/ai-receptionist-cta-001.jpg')`
              }}
            />
          </div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
              Ready to Get Your AI Receptionist?
            </h2>
            <p className="text-xl mb-8 text-white/90 leading-relaxed">
              Join hundreds of small businesses that have transformed their customer service with AI. Start your free consultation today and see how an AI Receptionist can help your business grow.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Schedule Your Free Consultation
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </section>

        <Footer
          onOpenService={(title) => setActiveServiceModal(title)}
          onOpenIndustries={() => setShowIndustriesModal(true)}
          onOpenHowItWorks={() => setShowHowItWorksModal(true)}
        />

        {activeServiceModal && (
          <ServiceModal
            title={activeServiceModal}
            onClose={() => setActiveServiceModal(null)}
          />
        )}

        {showIndustriesModal && (
          <IndustriesModal onClose={() => setShowIndustriesModal(false)} />
        )}

        {showHowItWorksModal && (
          <HowItWorksModal onClose={() => setShowHowItWorksModal(false)} />
        )}

        <CalendarModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
        />
      </div>
    </>
  );
}